import React from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Dialog,
  HStack,
  Portal,
  Stack,
  Text,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { ReactComponent as Warning } from 'assets/icons/warning.svg';
import {
  useContactSendOtp,
  useCreateContact,
  useTimer,
  useVerifyContact,
} from 'hooks';
import useInputAttributes from 'hooks/useInputAttributes';
import { useTranslation } from 'localization';

import {
  Button,
  DIALOG_ACTION_BUTTON_PROPS,
  Input,
  SERVICE_CARD_BORDER_RADIUS,
  ServiceDialogBody,
  ServiceDialogCloseTrigger,
  ServiceDialogFooter,
  ServiceDialogHeader,
  serviceDialogShellProps,
} from 'components';
import { countryCode, yup } from 'utils';

const VerifyNumber = () => {
  const { t } = useTranslation('component.dashboard.verifyNumber');
  const navigate = useNavigate();
  const [step, setStep] = React.useState<'init' | 'verify'>('init');
  const [selectedCountry, setSelectedCountry] = React.useState<string>('');
  const [submittedContactId, setSubmittedContactId] =
    React.useState<string>('');
  const { open, onOpen, onClose } = useDisclosure();

  const { mutateAsync: submitContact, isPending: submitLoading } =
    useCreateContact();
  const { mutateAsync: sendOtp, isPending: sendOtpLoading } =
    useContactSendOtp();
  const { mutateAsync: verifyContact, isPending: verifyLoading } =
    useVerifyContact();

  const { counter, setCounter } = useTimer(0);

  const closeHandler = () => {
    onClose();
    setStep('init');
    setSubmittedContactId('');
    setSelectedCountry('');
    resetSubmit();
    resetVerify();
    setCounter(0);
    navigate(0);
  };
  const formFieldsIdentity = {
    yup: {
      country: yup.string().label(t('form.country')).required(),
      identity: yup.string().label(t('form.mobile')).required(),
    },
    attributes: {
      country: t('form.country'),
      identity: t('form.mobile'),
    },
  };

  const {
    register: registerSubmit,
    handleSubmit,
    reset: resetSubmit,
    resetField: resetSubmitField,
    formState: { errors: errorsSubmit },
  } = useForm({
    resolver: yupResolver(yup.object().shape(formFieldsIdentity.yup)),
    defaultValues: {},
  });

  const formPropsSubmit = useInputAttributes(
    registerSubmit,
    errorsSubmit,
    formFieldsIdentity.attributes
  );

  const onSubmit = async (data: { identity: string; country: string }) => {
    const payload: { identity: string; type: 'MOBILE' | 'EMAIL' } = {
      identity: data.country + data.identity,
      type: 'MOBILE',
    };
    submitContact(payload).then((res) => {
      if (res.id) {
        setSubmittedContactId(res.id);
        resetSubmit();
        setStep('verify');
        sendOtp({ id: res.id }).then(() => {
          setCounter(120);
        });
      }
    });
  };

  const formFieldsVerify = {
    yup: {
      code: yup.string().label(t('form.code')).required(),
    },
    attributes: {
      code: t('form.code'),
    },
  };

  const {
    register: registerVerify,
    handleSubmit: handleVerify,
    reset: resetVerify,

    formState: { errors: errorsVerify },
  } = useForm({
    resolver: yupResolver(yup.object().shape(formFieldsVerify.yup)),
    defaultValues: {},
  });

  const formPropsVerify = useInputAttributes(
    registerVerify,
    errorsVerify,
    formFieldsVerify.attributes
  );
  const onVerify = async (data: { code: string }) => {
    verifyContact({ data, id: submittedContactId }).then(() => {
      resetVerify();
      closeHandler();
    });
  };

  const setOtpHandler = () => {
    sendOtp({ id: submittedContactId }).then(() => {
      setCounter(120);
    });
  };

  const selectedFlag = countryCode.find(
    (item) => item.value === selectedCountry
  )?.flagSvg;

  return (
    <Box w="100%" bg="lightPrimary">
      <Stack
        justify="space-between"
        w="full"
        px={{ base: '6', lg: '10' }}
        py="7"
        borderRadius={SERVICE_CARD_BORDER_RADIUS}
        direction={{ base: 'column', lg: 'row' }}
        align="center"
      >
        <Stack direction={{ base: 'column', lg: 'row' }} gap="6" align="center">
          <Warning />
          <Text color="warning">{t('pleaseVerifyNumber')}</Text>
        </Stack>
        <Button
          variant="filled"
          title="Verify Number"
          w="167px"
          onClick={() => {
            onOpen();
          }}
        />
      </Stack>
      <Dialog.Root
        closeOnInteractOutside={false}
        open={open}
        onOpenChange={(e: { open: boolean }) => {
          if (!e.open) {
            closeHandler();
          }
        }}
      >
        <Portal>
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content {...serviceDialogShellProps()}>
              <ServiceDialogHeader>{t('addMobileTitle')}</ServiceDialogHeader>
              <ServiceDialogCloseTrigger />
              {step === 'init' ? (
                <form onSubmit={handleSubmit(onSubmit)}>
                  <ServiceDialogBody>
                    <VStack w="100%">
                      <Box w="100%">
                        <Input
                          {...formPropsSubmit.country}
                          isSelectBox
                          options={countryCode}
                          onChange={(
                            e: React.ChangeEvent<HTMLSelectElement>
                          ) => {
                            setSelectedCountry(e.target.value);
                            resetSubmitField('identity');
                          }}
                          leftValue={
                            selectedFlag ? (
                              <img
                                src={selectedFlag}
                                alt="-"
                                width="20px"
                                height="20px"
                              />
                            ) : (
                              ''
                            )
                          }
                        />
                      </Box>
                      <Box w="100%">
                        <Input
                          {...formPropsSubmit.identity}
                          leftValue={selectedCountry}
                        />
                      </Box>
                    </VStack>
                  </ServiceDialogBody>
                  <ServiceDialogFooter>
                    <HStack justifyContent="flex-end" gap={2} flexWrap="wrap">
                      <Button
                        variant="ghost"
                        title={t('dialogClose')}
                        onClick={() => closeHandler()}
                        {...DIALOG_ACTION_BUTTON_PROPS}
                      />
                      <Button
                        variant="filled"
                        title={t('dialogSubmit')}
                        type="submit"
                        isLoading={submitLoading}
                        {...DIALOG_ACTION_BUTTON_PROPS}
                      />
                    </HStack>
                  </ServiceDialogFooter>
                </form>
              ) : (
                <form onSubmit={handleVerify(onVerify)}>
                  <ServiceDialogBody>
                    <Box>
                      <Input
                        {...formPropsVerify.code}
                        textAlign="center"
                        placeholder="     -     -     -     -     -     -     "
                      />
                    </Box>
                    <Box mt={6} w="100%">
                      <Button
                        variant="filled"
                        w="100%"
                        title={
                          counter === 0
                            ? t('dialogSendCode')
                            : counter.toString()
                        }
                        onClick={() => setOtpHandler()}
                        isLoading={sendOtpLoading}
                        isDisabled={counter !== 0}
                        {...DIALOG_ACTION_BUTTON_PROPS}
                      />
                    </Box>
                  </ServiceDialogBody>
                  <ServiceDialogFooter>
                    <HStack justifyContent="flex-end" gap={2} flexWrap="wrap">
                      <Button
                        variant="ghost"
                        title={t('dialogClose')}
                        onClick={() => closeHandler()}
                        {...DIALOG_ACTION_BUTTON_PROPS}
                      />
                      <Button
                        variant="filled"
                        title={t('dialogSubmit')}
                        type="submit"
                        isLoading={verifyLoading}
                        {...DIALOG_ACTION_BUTTON_PROPS}
                      />
                    </HStack>
                  </ServiceDialogFooter>
                </form>
              )}
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>
    </Box>
  );
};
export default VerifyNumber;
